// Centralized date formatting utilities
// Format ISO timestamp (or Date) to "MM/DD/YYYY H:MM:SS" using local time.
// Returns '-' when input is falsy or invalid.
export function formatTimestampMDYHMS(value?: string | number | Date): string {
  if (value === undefined || value === null || value === "") return "-";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();

  // No leading zero for hour to match requested example "2:30:00"
  const h = String(date.getHours());
  const min = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");

  return `${mm}/${dd}/${yyyy} ${h}:${min}:${ss}`;
}

/**
 * Converts a date string (MM/DD/YYYY or MM-DD-YYYY) to an ISO 8601 string
 * with the local timezone offset.
 *
 * @param {string}        dateStr  - Date in MM/DD/YYYY or MM-DD-YYYY format
 * @param {"start"|"end"} position - "start" → 00:00:00.000, "end" → 23:59:59.999
 * @returns {string} ISO 8601 string with local timezone offset (e.g. -08:00)
 *
 * ── Sample input / output (machine running in PST = UTC-08:00) ──────
 *
 *   convertToISOWithTimezone("03/24/2026", "start")
 *     → "2026-03-24T00:00:00.000-08:00"
 *
 *   convertToISOWithTimezone("03/24/2026", "end")
 *     → "2026-03-24T23:59:59.999-08:00"
 *
 *   convertToISOWithTimezone("03-24-2026", "start")   // dash separator
 *     → "2026-03-24T00:00:00.000-08:00"
 *
 *   convertToISOWithTimezone("03/24/2026")             // default = "start"
 *     → "2026-03-24T00:00:00.000-08:00"
 *
 *   convertToISOWithTimezone("")                       // empty → empty
 *     → ""
 *
 *   convertToISOWithTimezone("bad-input")              // unparseable → passthrough
 *     → "bad-input"
 *
 * ── Bug fix note ─────────────────────────────────────────────────────
 * The original code used: date.toISOString().replace("Z", offset)
 * That is wrong — toISOString() returns UTC time, so replacing Z with a
 * local offset produces a semantically incorrect timestamp (e.g. for PST
 * midnight you'd get T08:00:00-08:00 instead of T00:00:00-08:00).
 * This version builds the ISO string from local time components directly.
 */
export function convertToISOWithTimezone(
  dateStr: string,
  position: "start" | "end" = "start"
): string {
  if (!dateStr) return "";

  const normalized = dateStr.replaceAll("-", "/");
  const parts = normalized.split("/");
  if (parts.length !== 3) return dateStr;

  const [mm, dd, yyyy] = parts;

  const month = Number.parseInt(mm, 10) - 1;
  const day = Number.parseInt(dd, 10);
  const year = Number.parseInt(yyyy, 10);

  if ([month, day, year].some(Number.isNaN)) return dateStr;

  const date = new Date(year, month, day);

  if (position === "start") {
    date.setHours(0, 0, 0, 0);
  } else {
    date.setHours(23, 59, 59, 999);
  }

  const pad = (n: number, len = 2) => String(n).padStart(len, "0");
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absOffset = Math.abs(offsetMinutes);
  const offset = `${sign}${pad(Math.floor(absOffset / 60))}:${pad(absOffset % 60)}`;

  // Build from local time components so wall-clock time matches the offset
  const localISO = (
    `${date.getFullYear()}` +
    `-${pad(date.getMonth() + 1)}` +
    `-${pad(date.getDate())}` +
    `T${pad(date.getHours())}` +
    `:${pad(date.getMinutes())}` +
    `:${pad(date.getSeconds())}` +
    `.${pad(date.getMilliseconds(), 3)}` +
    offset
  );
  return new Date(localISO).toISOString();
}
// passthrough for YYYY-MM-DD to MM/DD/YYYY without time
export function formatDateYMDToMDY(value?: string): string {
  if (!value) return "";
  // If already has slashes, return as-is
  if (value.includes("/")) return value;
  const parts = value.split("-");
  if (parts.length < 3) return value;
  const [y, m, d] = parts;
  return `${m.padStart(2, "0")}/${d.padStart(2, "0")}/${y}`;
}

// Format ISO timestamp (or Date) to "MM/DD/YYYY" (no time).
// Returns '-' when input is falsy or invalid.
export function formatTimestampMDY(value?: string | number | Date): string {
  if (value === undefined || value === null || value === "") return "-";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();

  return `${mm}/${dd}/${yyyy}`;
}

/**
 * Format ISO timestamp to readable date format
 * Format: Jun 06, 2024 11:31:43
 */
export const formatTimestamp = (timestamp: string | undefined | null): string => {
  if (!timestamp) return "";
  try {
    const date = new Date(timestamp);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    };
    return date.toLocaleDateString("en-US", options).replace(",", "");
  } catch (error) {
    console.error("Error formatting timestamp:", error);
    return timestamp;
  }
};

